import { useSettingsStore } from '../stores/settingsStore';
import { logger } from '../utils/logger';
import type { RadioStation } from '../types/radio';

type NotificationType = 'feature' | 'nowPlaying' | 'sleepTimer' | 'favorite' | 'error' | 'success';

interface NotificationTheme {
  icon: string;
  badge?: string;
  vibrate?: number[];
  requireInteraction?: boolean;
  silent?: boolean;
}

// Extended NotificationOptions to include our custom properties
interface ExtendedNotificationOptions extends NotificationOptions {
  renotify?: boolean;
  image?: string;
}

// Define the expected shape of settings we use
interface NotificationSettings {
  newFeatureNotifications?: boolean;
  pushNotifications?: boolean;
}

class NotificationService {
  private hasPermission: boolean = false;
  private themes: Record<NotificationType, NotificationTheme> = {
    feature: {
      icon: '/feature-icon.png',
      badge: '/badge.png',
      vibrate: [200, 100, 200],
      requireInteraction: true,
    },
    nowPlaying: {
      icon: '/radio.svg',
      silent: true,
    },
    sleepTimer: {
      icon: '/timer-icon.png',
      vibrate: [100, 50, 100],
    },
    favorite: {
      icon: '/heart-icon.png',
      silent: true,
    },
    error: {
      icon: '/error-icon.png',
      vibrate: [100, 50, 100],
    },
    success: {
      icon: '/success-icon.png',
      silent: true,
    }
  };

  constructor() {
    this.checkPermission();
  }

  private getSettings(): NotificationSettings {
    return useSettingsStore.getState() as unknown as NotificationSettings;
  }

  private async checkPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
      logger.warning('Notifications are not supported in this browser');
      return false;
    }

    if (Notification.permission === 'granted') {
      this.hasPermission = true;
      logger.info('✅ Notification permission already granted');
      return true;
    }

    if (Notification.permission !== 'denied') {
      try {
        const permission = await Notification.requestPermission();
        this.hasPermission = permission === 'granted';
        logger.info(`${this.hasPermission ? '✅' : '❌'} Notification permission ${this.hasPermission ? 'granted' : 'denied'}`);
        return this.hasPermission;
      } catch (error) {
        logger.error('❌ Error requesting notification permission:', error);
        return false;
      }
    }

    logger.warning('Notification permission previously denied');
    return false;
  }

  async requestPermission(): Promise<boolean> {
    return this.checkPermission();
  }

  private async showNotificationWithTheme(
    title: string,
    type: NotificationType,
    options: ExtendedNotificationOptions = {}
  ) {
    if (!this.hasPermission) {
      const granted = await this.checkPermission();
      if (!granted) return null;
    }

    try {
      const theme = this.themes[type];
      const notification = new Notification(title, {
        ...theme,
        ...options,
      } as NotificationOptions);

      notification.onclick = () => {
        window.focus();
        notification.close();
      };

      logger.info(`🔔 Showing ${type} notification: ${title}`);
      return notification;
    } catch (error) {
      logger.error(`❌ Failed to show ${type} notification:`, error);
      return null;
    }
  }

  async showFeatureNotification(feature: string) {
    const settings = this.getSettings();
    const newFeatureNotifications = settings.newFeatureNotifications ?? true;

    if (!newFeatureNotifications) {
      logger.info('ℹ️ Feature notifications are disabled');
      return null;
    }

    return this.showNotificationWithTheme('New Feature Available!', 'feature', {
      body: `Check out our new ${feature} feature!`,
      tag: 'feature-update',
      renotify: true,
    });
  }

  async showNowPlaying(station: RadioStation) {
    const settings = this.getSettings();
    const pushNotifications = settings.pushNotifications ?? true;

    if (!pushNotifications) {
      logger.info('ℹ️ Push notifications are disabled');
      return null;
    }

    return this.showNotificationWithTheme('Now Playing', 'nowPlaying', {
      body: `${station.name}${station.country ? ` from ${station.country}` : ''}`,
      tag: 'now-playing',
      image: station.favicon || undefined,
    });
  }

  async showSleepTimer(minutesLeft: number) {
    const settings = this.getSettings();
    const pushNotifications = settings.pushNotifications ?? true;

    if (!pushNotifications) {
      logger.info('ℹ️ Push notifications are disabled');
      return null;
    }

    return this.showNotificationWithTheme('Sleep Timer', 'sleepTimer', {
      body: `Radio will stop in ${minutesLeft} minute${minutesLeft === 1 ? '' : 's'}`,
      tag: 'sleep-timer',
      renotify: true,
    });
  }

  async showFavoriteNotification(station: RadioStation, added: boolean) {
    const settings = this.getSettings();
    const pushNotifications = settings.pushNotifications ?? true;

    if (!pushNotifications) {
      logger.info('ℹ️ Push notifications are disabled');
      return null;
    }

    return this.showNotificationWithTheme(
      added ? 'Added to Favorites' : 'Removed from Favorites',
      'favorite',
      {
        body: station.name,
        tag: 'favorite',
        image: station.favicon || undefined,
      }
    );
  }

  async showErrorNotification(message: string) {
    const settings = this.getSettings();
    const pushNotifications = settings.pushNotifications ?? true;

    if (!pushNotifications) {
      logger.info('ℹ️ Push notifications are disabled');
      return null;
    }

    return this.showNotificationWithTheme('Error', 'error', {
      body: message,
      tag: 'error',
      renotify: true,
    });
  }

  async showSuccessNotification(message: string) {
    const settings = this.getSettings();
    const pushNotifications = settings.pushNotifications ?? true;

    if (!pushNotifications) {
      logger.info('ℹ️ Push notifications are disabled');
      return null;
    }

    return this.showNotificationWithTheme('Success', 'success', {
      body: message,
      tag: 'success',
    });
  }
}

export const notificationService = new NotificationService(); 